import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { request } from 'utilities/graph';
import { getVideo } from 'graphql/queries';
import Loading from 'components/Loading';
import cache from 'utilities/cache';
import TagChip from 'components/TagChip';
import { sortBy } from 'utilities/sorting';
import { NEW_VIDEO_UPDATED_EVENT, NOTIFICATION_CATEGORIES } from '../constants/videoNotification';
import { Storage } from 'aws-amplify';

const useStyles = makeStyles({
  root: {
    maxWidth: '60vw',
    margin: '0 auto',
    padding: 20,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  videoContainer: {
    position: 'relative',
    paddingBottom: '56.25%', // 16:9 aspect ratio
    height: 0,
    overflow: 'hidden',
    marginBottom: 20,
  },
  videoFrame: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    border: 'none',
  },
  pdfContainer: {
    position: 'relative',
    paddingBottom: '90%', // 16:9 aspect ratio
    height: 0,
    overflow: 'scroll',
    marginBottom: 20,
  },
});

export default function InstructionVideo() {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const pathPrefix = cache.get('app:pathPrefix');
  const pathParts = location.pathname.split('/');
  const id = pathParts.pop();
  const category = pathParts[pathParts.length - 1];
  const [video, setVideo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pdfSignedUrl, setPdfSignedUrl] = useState(null);


  useEffect(() => {
    if (category) {
      cache.set('app:viewedInstructionVideoCategory', category);
    }
  }, [category]);

  // 如果機構方觀看影片，若是比較新的影片則更新紅點
  useEffect(() => {
    const shouldUpdateCache = video &&
      pathPrefix === 'facility' &&
      NOTIFICATION_CATEGORIES.includes(video.category) &&
      (new Date(video.createdAt) > new Date(cache.get('app:facility:notifiedNewestVideoTime')));

    if (shouldUpdateCache) {
      cache.set('app:facility:notifiedNewestVideoTime', video.createdAt);
      // 觸發 cache 更新事件
      window.dispatchEvent(new Event(NEW_VIDEO_UPDATED_EVENT));
    }
  }, [video, pathPrefix]);

  useEffect(() => {
    const fetchVideo = async () => {
      try {
        const result = await request(getVideo, { id });
        setVideo(result.data.getVideo);
        if (result.data.getVideo.mediaType === 'PDF') {
          const signedUrl = await Storage.get(result.data.getVideo.videoUrl);
          setPdfSignedUrl(signedUrl);
        }
      } catch (error) {
        console.error('Error fetching video:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (!id) {
      console.error('No video ID found in URL');
      setIsLoading(false);
      return;
    }

    fetchVideo();
  }, [id, location]);


  if (isLoading) {
    return <Loading fullScreen={false} />;
  }

  if (!video) {
    return <div>找不到影片</div>;
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.push(`/${pathPrefix}/instructionCenter`)}
        >
          返回教學中心
        </Button>
      </div>
      <Card>
        <div className={video.mediaType === 'PDF' ? classes.pdfContainer : classes.videoContainer}>
          <iframe
            className={classes.videoFrame}
            src={pdfSignedUrl || video.videoUrl}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <CardContent>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
            <div>
              <Typography gutterBottom variant="h5" component="h2">
                {video.title}
              </Typography>
              <Typography variant="body2" color="textSecondary" component="p">
                {video.description}
              </Typography>
            </div>
            <div>
              <React.Fragment>
                {video.tags.items
                  .map(({ tag }) => tag)
                  .sort(sortBy('name'))
                  .sort(sortBy('subcategory'))
                  .sort(sortBy('category'))
                  .map((tag, index)=>(
                    <TagChip
                      key={index}
                      tag={tag}
                    />
                  ))}
              </React.Fragment>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
